import { FileText, Download, Calendar, TrendingUp } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { ComingSoonWatermark } from '@/components/ComingSoonWatermark';
import { useNavigation } from '@/contexts/NavigationContext';
import { cn } from '@/lib/utils';

export function ReportsPage() {
  const { sectionColor } = useNavigation();

  return (
    <div className="space-y-6">
      <ComingSoonWatermark
        pageKey="report-generation"
        icon={FileText}
        title="Report Generation Coming Soon"
        description="Automated report generation, custom templates, and scheduled exports will be available soon."
      />
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className={cn("text-3xl font-bold", sectionColor)}>Reports</h1>
          <p className="text-neutral-400 mt-1">
            Generate regulatory and executive reports
          </p>
        </div>
        <button className="btn-primary">
          Generate Report
        </button>
      </div>

      {/* Report Templates */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <Card className="p-6 hover:border-primary-500/50 transition-colors cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <FileText className="text-primary-400" size={24} />
            <Calendar className="text-neutral-500" size={20} />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Executive Summary</h3>
          <p className="text-sm text-neutral-400 mb-4">
            Board-ready risk summary with KPIs and trend analysis
          </p>
          <button className="btn-outline w-full text-sm">
            Generate
          </button>
        </Card>

        <Card className="p-6 hover:border-primary-500/50 transition-colors cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <FileText className="text-primary-400" size={24} />
            <Download className="text-neutral-500" size={20} />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">DORA Compliance</h3>
          <p className="text-sm text-neutral-400 mb-4">
            Regulatory compliance report for DORA framework
          </p>
          <button className="btn-outline w-full text-sm">
            Generate
          </button>
        </Card>

        <Card className="p-6 hover:border-primary-500/50 transition-colors cursor-pointer">
          <div className="flex items-center justify-between mb-4">
            <FileText className="text-primary-400" size={24} />
            <TrendingUp className="text-neutral-500" size={20} />
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">Risk Trends</h3>
          <p className="text-sm text-neutral-400 mb-4">
            Historical risk analysis and trend forecasting
          </p>
          <button className="btn-outline w-full text-sm">
            Generate
          </button>
        </Card>
      </div>

      {/* Recent Reports Table */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Reports</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-neutral-800">
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Report Name</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Type</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Generated</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Period</th>
                <th className="text-left py-3 px-4 text-sm font-medium text-neutral-400">Status</th>
                <th className="text-right py-3 px-4 text-sm font-medium text-neutral-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">Q3 2024 Executive Summary</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Executive</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-15</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Q3 2024</td>
                <td className="py-3 px-4"><span className="badge-success">Completed</span></td>
                <td className="py-3 px-4 text-right">
                  <button className="text-primary-400 hover:text-primary-300 text-sm">
                    <Download size={16} className="inline" /> Download
                  </button>
                </td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">DORA Annual Compliance</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Compliance</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-28</td>
                <td className="py-3 px-4 text-sm text-neutral-300">FY 2024</td>
                <td className="py-3 px-4"><span className="badge-success">Completed</span></td>
                <td className="py-3 px-4 text-right">
                  <button className="text-primary-400 hover:text-primary-300 text-sm">
                    <Download size={16} className="inline" /> Download
                  </button>
                </td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">Risk Trend Analysis</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Analytics</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-11-02</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Oct 2024</td>
                <td className="py-3 px-4"><span className="badge-primary">Processing</span></td>
                <td className="py-3 px-4 text-right">
                  <span className="text-neutral-500 text-sm">Generating...</span>
                </td>
              </tr>
              <tr className="border-b border-neutral-800/50 hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">Control Effectiveness Report</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Audit</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-20</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Oct 2024</td>
                <td className="py-3 px-4"><span className="badge-success">Completed</span></td>
                <td className="py-3 px-4 text-right">
                  <button className="text-primary-400 hover:text-primary-300 text-sm">
                    <Download size={16} className="inline" /> Download
                  </button>
                </td>
              </tr>
              <tr className="hover:bg-neutral-900/50">
                <td className="py-3 px-4 text-sm text-white">Third-Party Risk Summary</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Supplier</td>
                <td className="py-3 px-4 text-sm text-neutral-300">2024-10-10</td>
                <td className="py-3 px-4 text-sm text-neutral-300">Q3 2024</td>
                <td className="py-3 px-4"><span className="badge-success">Completed</span></td>
                <td className="py-3 px-4 text-right">
                  <button className="text-primary-400 hover:text-primary-300 text-sm">
                    <Download size={16} className="inline" /> Download
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
